import { ChangeDetectionStrategy, Component, computed, HostListener, inject, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ProductionCommandBase } from '@src/suite/common/store/story/models/story.models';
import { ProductionCommandsTitleKey, ProductionCommandType } from '@src/suite/common/utils/ml-2-html-utils';
import { catchError, Subject, take } from 'rxjs';

import { AnchorCommandDialogComponent } from './anchor-command-dialog/anchor-command-dialog';
import { DurationCommandDialogComponent } from './duration-command-dialog/duration-command-dialog';
import { InsertTakeCommandDialogComponent } from './insert-take-command-dialog/insert-take-command-dialog';
import { NotesCommandDialogComponent } from './notes-command-dialog/notes-command-dialog';

@Component({
    selector: 'st-production-command-dialog',
    imports: [AnchorCommandDialogComponent, DurationCommandDialogComponent, InsertTakeCommandDialogComponent, LetDirective, MatButton, MatButtonToggleGroup, NotesCommandDialogComponent],
    templateUrl: './production-command-dialog.html',
    styleUrl: './production-command-dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'st-production-command-dialog' },
})
export class ProductionCommandDialogComponent implements OnDestroy {
    data = inject<{
        command: ProductionCommandBase;
        commands: ProductionCommandBase[];
        editMode: boolean;
    }>(MAT_DIALOG_DATA);

    private store = inject(Store);
    private modalService = inject(ModalService);
    private dialogRef = inject(MatDialogRef<ProductionCommandDialogComponent>);

    public ProductionCommandType = ProductionCommandType;
    public languageSettings = this.store.selectSignal(selectI18N);
    // TODO make command and commands in one signal []
    public readonly command = signal<ProductionCommandBase>(null);
    public readonly commands = signal<ProductionCommandBase[]>(null);
    public readonly editMode = signal<boolean>(false);
    public destroyed$ = new Subject<void>();

    readonly commandType = computed(() => {
        if (this.commands()) {
            return this.commands()[0]?.commandType?.toUpperCase();
        }
        return (this.command() as ProductionCommandBase)?.commandType?.toUpperCase();
    });
    readonly title = computed(() => {
        return this.languageSettings()[ProductionCommandsTitleKey[this.commandType()]];
    });

    onInsert = new Subject<ProductionCommandBase | ProductionCommandBase[]>();
    onUpdate = new Subject<ProductionCommandBase | ProductionCommandBase[]>();
    onDelete = new Subject<void>();

    constructor() {
        this.command.set(this.data.command);
        this.editMode.set(!!this.data.editMode);
        this.commands.set(this.data.commands);
    }

    @HostListener('document:keydown.enter', ['$event'])
    onKeydownEnter(event: Event): void {
        event.preventDefault();

        // Only handle Enter key if it's not coming from a child component's input
        // This prevents double triggering when child components already handle Enter
        const tagName = (event.target as HTMLElement).tagName.toLowerCase();
        if (tagName === 'input' || tagName === 'mat-select') return;

        if (this.editMode()) {
            this.updateCommand();
        } else {
            this.insertCommand();
        }
    }

    public cancel() {
        this.dialogRef.close();
    }

    public updateCommand() {
        if (this.command()) {
            this.onUpdate.next(this.command());
        } else if (this.commands()) {
            this.onUpdate.next(this.commands());
        }
        this.dialogRef.close();
    }

    public insertCommand() {
        if (this.command()) {
            this.onInsert.next(this.command());
        } else if (this.commands()) {
            this.onInsert.next(this.commands());
        }
        this.dialogRef.close();
    }

    async deleteCommand() {
        this.modalService
            .openModal({
                title: `${this.languageSettings().PSTWebConfirmDeletePart1} item?`,
                content: '',
                yesString: this.languageSettings().PSTWebOkButtonLabel,
                noString: this.languageSettings().PSTWebCancelButtonLabel,
            })
            .result.pipe(
                take(1),
                catchError(() => {
                    return [ModalResponseCodes.CANCEL];
                })
            )
            .subscribe(res => {
                if (res === ModalResponseCodes.CANCEL) {
                    this.dialogRef.close();
                } else {
                    this.onDelete.next();
                    this.dialogRef.close();
                }
            });
    }

    commandChanged(commands: ProductionCommandBase | ProductionCommandBase[]) {
        if (!Array.isArray(commands)) {
            this.command.set(commands);
        } else {
            this.commands.set(commands);
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
